import React, { useEffect, useMemo, useState } from "react";
import config from "../config.js";
import { detailToString } from '../utils/apiError';

/**
 * RevisionApprovalPortal — PUBLIC customer-facing page at /approve/<token>.
 *
 * No site auth, no app chrome: the 256-bit token in the URL is the only
 * credential and the /portal/revision-approval/* API routes are
 * API-key-exempt. Shows the same content as the printed Part Revision
 * Change Approval form (change description, Rev-vs-Rev comparison with
 * drawings, pricing-impact statement, affected orders) and lets the
 * customer Approve with a typed name/title e-signature — which stores the
 * APPROVED ELECTRONICALLY archival PDF on the part in JobQ — or request
 * changes with a comment.
 */
export default function RevisionApprovalPortal() {
  const API_BASE = config.API_BASE_URL;
  const token = window.location.pathname.split("/approve/")[1] || "";

  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {status, message, signed_url}

  const [approverName, setApproverName] = useState("");
  const [approverTitle, setApproverTitle] = useState("");
  const [comments, setComments] = useState("");
  const [attested, setAttested] = useState(false);
  const [showChanges, setShowChanges] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/portal/revision-approval/${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setLoadErr(String(e.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE, token]);

  const specFields = useMemo(() => ([
    { label: "Dimensions", fn: s => (s.size_length && s.size_width) ? `${s.size_length}" × ${s.size_width}"` : (s.size_length ? `${s.size_length}"` : "—") },
    { label: "Thickness", fn: s => s.thickness || "—" },
    { label: "Material", fn: s => s.material_type || "—" },
    { label: "Finish", fn: s => s.surface_finish || "—" },
    { label: "Weight", fn: s => s.weight_lbs ? `${s.weight_lbs} lb` : "—" },
    { label: "Process", fn: s => (s.process_steps || "").split(",").map(x => x.trim()).filter(Boolean).join("  →  ") || "—" },
    { label: "Description", fn: s => s.description || "—" },
  ]), []);

  async function respond(decision) {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/portal/revision-approval/${encodeURIComponent(token)}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          decision,
          approver_name: approverName.trim() || null,
          approver_title: approverTitle.trim() || null,
          comments: comments.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(body.detail) || `HTTP ${res.status}`);
      setResult(body);
      window.scrollTo(0, 0);
    } catch (e) {
      alert(`Sorry — that didn't go through: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  }

  const shell = (children) => (
    <div style={{ background: "#f2f4f7", minHeight: "100vh", padding: "24px 12px" }}>
      <div className="mx-auto bg-white shadow-sm rounded" style={{ maxWidth: "860px", padding: "28px 30px" }}>
        {children}
        <div className="text-center text-muted mt-4" style={{ fontSize: "0.75rem" }}>
          KanCraft MFG · 583 E 47 Hwy, Girard, Kansas 66743 · www.KanCraftMFG.com
        </div>
      </div>
    </div>
  );

  if (loadErr) {
    return shell(
      <div className="text-center py-5">
        <h4>Approval link not found</h4>
        <p className="text-muted">This link is invalid or no longer available. Please contact KanCraft MFG for a new approval link.</p>
      </div>
    );
  }
  if (!data) {
    return shell(<div className="text-center py-5 text-muted">Loading…</div>);
  }

  const status = result?.status || data.status;
  const changed = (data.old_spec && data.new_spec)
    ? specFields.filter(f => f.fn(data.old_spec) !== f.fn(data.new_spec)).map(f => f.label)
    : [];

  const header = (
    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 border-bottom pb-3 mb-3">
      <div className="d-flex align-items-center gap-3">
        {data.company?.logo_url && (
          <img src={data.company.logo_url} alt="KanCraft MFG" style={{ height: "58px" }} />
        )}
        <div>
          <div className="fw-bold" style={{ fontSize: "1.05rem" }}>{data.company?.name}</div>
          <div className="text-muted" style={{ fontSize: "0.8rem" }}>{data.company?.address}</div>
        </div>
      </div>
      <div className="text-end">
        <div className="fw-bold" style={{ fontSize: "1.1rem" }}>PART REVISION<br />CHANGE APPROVAL</div>
        <div className="text-muted" style={{ fontSize: "0.8rem" }}>Form #: {data.form_number}</div>
      </div>
    </div>
  );

  if (status === "approved" || status === "signed_on_paper") {
    return shell(
      <>
        {header}
        <div className="text-center py-4">
          <div style={{ fontSize: "3rem" }}>✅</div>
          <h4 className="mt-2">Revision change approved</h4>
          <p className="mb-1">
            <strong>{data.part_number}</strong>: Rev {data.old_revision_code} → Rev {data.new_revision_code}
          </p>
          {(result?.message || data.approver_name) && (
            <p className="text-muted">
              {result?.message || `Approved by ${data.approver_name}${data.approver_title ? `, ${data.approver_title}` : ""}${data.approved_at ? ` on ${new Date(data.approved_at).toLocaleDateString()}` : ""}.`}
            </p>
          )}
          {result?.signed_url && (
            <a className="btn btn-outline-primary btn-sm" href={result.signed_url} target="_blank" rel="noreferrer">
              View the approval record (PDF)
            </a>
          )}
          <p className="text-muted mt-3" style={{ fontSize: "0.85rem" }}>
            A signed record is stored with KanCraft MFG. No further action is needed.
          </p>
        </div>
      </>
    );
  }

  if (status === "expired") {
    return shell(
      <>
        {header}
        <div className="text-center py-4">
          <h4>This approval link has expired</h4>
          <p className="text-muted">Please contact KanCraft MFG and we'll send a fresh link for<br />
            <strong>{data.part_number}</strong> Rev {data.old_revision_code} → Rev {data.new_revision_code}.</p>
        </div>
      </>
    );
  }

  return shell(
    <>
      {header}

      {(status === "changes_requested" || result?.status === "changes_requested") && (
        <div className="alert alert-info py-2">
          {result?.message || `You requested changes${data.changes_requested_at ? ` on ${new Date(data.changes_requested_at).toLocaleDateString()}` : ""} — KanCraft has been notified. You can still approve below once resolved.`}
        </div>
      )}

      {/* Part information */}
      <div className="border rounded p-3 mb-3">
        <div className="row g-2" style={{ fontSize: "0.92rem" }}>
          <div className="col-sm-6"><span className="text-muted small">CUSTOMER</span><div>{data.customer_name || "—"}</div></div>
          <div className="col-sm-6"><span className="text-muted small">PART NUMBER</span><div className="fw-bold">{data.part_number}</div></div>
          <div className="col-sm-6"><span className="text-muted small">REVISION CHANGE</span>
            <div className="fw-bold" style={{ fontSize: "1.05rem" }}>Rev {data.old_revision_code} &nbsp;→&nbsp; Rev {data.new_revision_code}</div>
          </div>
          <div className="col-sm-6"><span className="text-muted small">CUSTOMER PART #</span><div>{data.customer_part_number || "—"}</div></div>
        </div>
      </div>

      {/* Description of change */}
      <h6 className="fw-bold">DESCRIPTION OF CHANGE</h6>
      <div className="border rounded p-3 mb-3" style={{ whiteSpace: "pre-wrap", fontSize: "0.95rem" }}>
        {data.change_description || <span className="text-muted">See comparison below.</span>}
      </div>

      {/* Comparison */}
      {data.old_spec && data.new_spec && (
        <>
          <h6 className="fw-bold">REVISION COMPARISON</h6>
          <div className="row g-3 mb-2">
            {[["PREVIOUS", data.old_revision_code, data.old_spec, "#f8f9fa"], ["NEW", data.new_revision_code, data.new_spec, "#eefaf1"]].map(([tag, code, spec, bg]) => (
              <div className="col-md-6" key={tag}>
                <div className="border rounded h-100" style={{ background: bg }}>
                  <div className="px-3 py-2 border-bottom d-flex justify-content-between">
                    <strong>REV {code}</strong><span className="text-muted small">{tag}</span>
                  </div>
                  <div className="p-3">
                    {spec.image_url ? (
                      <img src={spec.image_url} alt={`Rev ${code} drawing`} className="d-block mx-auto mb-2" style={{ maxWidth: "100%", maxHeight: "230px" }} />
                    ) : (
                      <div className="text-center text-muted py-4 border rounded mb-2" style={{ fontSize: "0.85rem" }}>[Drawing not available]</div>
                    )}
                    <table className="table table-sm mb-0" style={{ fontSize: "0.82rem" }}>
                      <tbody>
                        {specFields.map(f => (
                          <tr key={f.label}>
                            <td className="text-muted" style={{ width: "38%" }}>{f.label}</td>
                            <td className={changed.includes(f.label) ? "fw-bold" : ""} style={changed.includes(f.label) ? { color: "#c05000" } : {}}>
                              {f.fn(spec)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-muted mb-3" style={{ fontSize: "0.78rem" }}>
            {changed.length > 0
              ? <>Highlighted values differ between revisions: <strong>{changed.join(", ")}</strong>.</>
              : "No specification-field changes — drawing/geometry change only (see description above)."}
          </div>
        </>
      )}

      {/* Pricing impact */}
      <h6 className="fw-bold">PRICING IMPACT</h6>
      <div className="border rounded p-3 mb-3" style={{ fontSize: "0.92rem" }}>
        {data.pricing_impact === "unchanged" && <span>☑ No change to quoted pricing</span>}
        {data.pricing_impact === "revised" && <span>☑ Pricing revised — see updated quote #{data.pricing_quote_ref || "—"}</span>}
        {!data.pricing_impact && <span className="text-muted">Not specified — contact KanCraft with any pricing questions.</span>}
      </div>

      {/* Open orders */}
      <h6 className="fw-bold">OPEN ORDERS AFFECTED BY THIS CHANGE</h6>
      <div className="border rounded p-3 mb-4" style={{ fontSize: "0.9rem" }}>
        {(data.open_orders || []).length === 0 && (data.open_quotes || []).length === 0 && (
          <span className="text-muted">None on record at this time.</span>
        )}
        <ul className="mb-0 ps-3">
          {(data.open_orders || []).map(o => (
            <li key={o.order_number}>{o.order_number}{o.customer_po ? ` — Customer PO ${o.customer_po}` : ""} ({o.status})</li>
          ))}
          {(data.open_quotes || []).map(q => (
            <li key={q.quote_id}>Quote #{q.quote_id} ({q.status})</li>
          ))}
        </ul>
        <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>
          Order and quote numbers are unchanged — only the part revision is updated.
        </div>
      </div>

      {/* Approval */}
      <div className="border rounded p-3" style={{ background: "#fbfcfd" }}>
        <h6 className="fw-bold mb-2">APPROVE THIS REVISION CHANGE</h6>
        <div className="row g-2 mb-2">
          <div className="col-sm-6">
            <label className="form-label small mb-1">Your full name <span className="text-danger">*</span></label>
            <input className="form-control" value={approverName} onChange={e => setApproverName(e.target.value)} placeholder="Jane Smith" />
          </div>
          <div className="col-sm-6">
            <label className="form-label small mb-1">Title</label>
            <input className="form-control" value={approverTitle} onChange={e => setApproverTitle(e.target.value)} placeholder="Purchasing Manager" />
          </div>
        </div>
        <div className="form-check mb-2" style={{ fontSize: "0.85rem" }}>
          <input className="form-check-input" type="checkbox" id="attestChk" checked={attested} onChange={e => setAttested(e.target.checked)} />
          <label className="form-check-label" htmlFor="attestChk">{data.attestation}</label>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <button
            className="btn btn-success fw-bold px-4"
            disabled={busy || !attested || approverName.trim().length < 2}
            onClick={() => respond("approve")}
          >
            {busy ? "Submitting…" : `Approve Rev ${data.new_revision_code}`}
          </button>
          <button className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setShowChanges(v => !v)}>
            Request changes instead…
          </button>
          {data.expires_at && (
            <span className="text-muted ms-auto" style={{ fontSize: "0.78rem" }}>
              Link expires {new Date(data.expires_at).toLocaleDateString()}
            </span>
          )}
        </div>
        {showChanges && (
          <div className="mt-3 border-top pt-2">
            <label className="form-label small mb-1">What needs to change? <span className="text-danger">*</span></label>
            <textarea className="form-control mb-2" rows={3} value={comments} onChange={e => setComments(e.target.value)}
              placeholder="Describe the issue with this revision…" />
            <button className="btn btn-warning btn-sm fw-bold" disabled={busy || comments.trim().length < 3}
              onClick={() => respond("request_changes")}>
              Send to KanCraft
            </button>
          </div>
        )}
      </div>
    </>
  );
}
